/**
 * ff-sticky-purchase.js
 *
 * The sticky purchase bar is a fully independent section — it cannot assume
 * the product hero exists earlier in the DOM (Theme Editor lets merchants
 * reorder, remove, or duplicate sections freely). It listens for the
 * namespaced `ff:*` events dispatched by ff-product-purchase.js and only
 * reacts to events for the same product ID, and shows/hides itself based on
 * scroll position relative to the hero's Add to Cart button when available.
 */
(function () {
  'use strict';

  var CART_ADD_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  var standardEventsPromise = null;
  function loadStandardEvents() {
    if (!standardEventsPromise) {
      standardEventsPromise = import('@shopify/events').catch(function () {
        return null;
      });
    }
    return standardEventsPromise;
  }

  function FFStickyBar(root) {
    this.root = root;
    this.productId = root.dataset.productId;
    this.bar = qs(root, '[data-ff-sticky-bar-el]');
    this.priceEl = qs(root, '[data-ff-sticky-price]');
    this.titleEl = qs(root, '[data-ff-sticky-title]');
    this.mediaEl = qs(root, '[data-ff-sticky-media] img');
    this.ctaButton = qs(root, '[data-ff-sticky-cta]');
    this.availability = qs(root, '[data-ff-sticky-availability]');
    this.hiddenVariantInput = qs(root, '[data-ff-sticky-variant-input]');
    this.hiddenSellingPlanInput = qs(root, '[data-ff-sticky-selling-plan-input]');
    this.hiddenQtyInput = qs(root, '[data-ff-sticky-qty-input]');

    this.currentVariantId = root.dataset.initialVariantId;
    this.currentSellingPlanId = null;
    this.currentQuantity = 1;
    this.isSubmitting = false;

    this.triggerSelector = root.dataset.triggerSelector || '[data-ff-add-button]';
    this.visibilityMode = root.dataset.visibilityTrigger || 'scroll-past-buy-button';

    this.onPurchaseStateChange = this.onPurchaseStateChange.bind(this);
    this.onCartAddSuccess = this.onCartAddSuccess.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onCtaClick = this.onCtaClick.bind(this);

    document.addEventListener('ff:purchase-state-change', this.onPurchaseStateChange);
    document.addEventListener('ff:cart-add-success', this.onCartAddSuccess);
    if (this.ctaButton) this.ctaButton.addEventListener('click', this.onCtaClick);

    this.setupVisibility();
  }

  FFStickyBar.prototype.destroy = function () {
    document.removeEventListener('ff:purchase-state-change', this.onPurchaseStateChange);
    document.removeEventListener('ff:cart-add-success', this.onCartAddSuccess);
    if (this.ctaButton) this.ctaButton.removeEventListener('click', this.onCtaClick);
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  };

  FFStickyBar.prototype.setupVisibility = function () {
    var self = this;
    if (this.visibilityMode === 'always') {
      this.setVisible(true);
      return;
    }
    if (this.visibilityMode === 'scroll-past-buy-button') {
      var trigger = document.querySelector(this.triggerSelector);
      if (trigger && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        });
        this.observer.observe(trigger);
        return;
      }
    }
    // Fallback: reveal after scrolling down a fixed distance.
    window.addEventListener('scroll', this.onScroll, { passive: true });
  };

  FFStickyBar.prototype.onScroll = function () {
    this.setVisible(window.scrollY > 600);
  };

  FFStickyBar.prototype.setVisible = function (visible) {
    if (this.bar) this.bar.setAttribute('data-visible', visible ? 'true' : 'false');
  };

  FFStickyBar.prototype.onPurchaseStateChange = function (evt) {
    var detail = evt.detail;
    if (!detail || String(detail.productId) !== String(this.productId)) return;

    this.currentVariantId = detail.variantId;
    this.currentSellingPlanId = detail.sellingPlanId;
    this.currentQuantity = detail.quantity || 1;

    // Keep the real (no-JS-fallback) hidden form in sync too, so a native
    // submit — if our click handler ever fails to attach — still adds the
    // variant/plan the shopper actually selected.
    if (this.hiddenVariantInput) this.hiddenVariantInput.value = this.currentVariantId;
    if (this.hiddenSellingPlanInput) {
      if (this.currentSellingPlanId) {
        this.hiddenSellingPlanInput.value = this.currentSellingPlanId;
        this.hiddenSellingPlanInput.disabled = false;
      } else {
        this.hiddenSellingPlanInput.value = '';
        this.hiddenSellingPlanInput.disabled = true;
      }
    }
    if (this.hiddenQtyInput) this.hiddenQtyInput.value = this.currentQuantity;

    if (this.priceEl && detail.priceHtml) this.priceEl.innerHTML = detail.priceHtml;
    if (this.mediaEl && detail.variantImage) this.mediaEl.src = detail.variantImage;
    if (this.ctaButton) {
      this.ctaButton.disabled = detail.available === false;
    }
    if (this.availability) {
      this.availability.textContent = detail.available === false ? (this.availability.dataset.soldOutLabel || 'Sold out') : '';
      this.availability.hidden = detail.available !== false;
    }
  };

  FFStickyBar.prototype.onCartAddSuccess = function (evt) {
    var detail = evt.detail;
    if (!detail || String(detail.productId) !== String(this.productId)) return;
    if (detail.sourceSectionId === this.root.dataset.sectionId) return; // we already handled our own add
  };

  FFStickyBar.prototype.onCtaClick = function (evt) {
    if (!this.currentVariantId) return; // no JS fallback: let the <a> or <form> submit natively
    evt.preventDefault();
    this.addToCart();
  };

  FFStickyBar.prototype.addToCart = function () {
    var self = this;
    if (this.isSubmitting || !this.currentVariantId) return;
    this.isSubmitting = true;
    var originalLabel = this.ctaButton.textContent;
    this.ctaButton.disabled = true;
    this.ctaButton.textContent = this.ctaButton.dataset.addingLabel || 'Adding…';

    var payload = { id: this.currentVariantId, quantity: this.currentQuantity || 1 };
    if (this.currentSellingPlanId) payload.selling_plan = this.currentSellingPlanId;

    var resolveStandardEvent = null;
    loadStandardEvents().then(function (mod) {
      if (!mod || !mod.CartLinesUpdateEvent) return;
      try {
        var created = mod.CartLinesUpdateEvent.createPromise();
        resolveStandardEvent = created.resolve;
        document.dispatchEvent(new mod.CartLinesUpdateEvent({ action: 'add', lines: [payload], promise: created.promise }));
      } catch (e) {
        /* best-effort */
      }
    });

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        return res.json().then(function (json) {
          return { ok: res.ok, json: json };
        });
      })
      .then(function (result) {
        self.isSubmitting = false;
        self.ctaButton.disabled = false;
        self.ctaButton.textContent = originalLabel;
        if (!result.ok) {
          if (resolveStandardEvent) resolveStandardEvent({ detail: { didError: true } });
          document.dispatchEvent(
            new CustomEvent('ff:cart-add-error', {
              bubbles: true,
              detail: { message: (result.json && result.json.description) || 'Could not add to cart.', sourceSectionId: self.root.dataset.sectionId },
            })
          );
          return;
        }
        if (resolveStandardEvent) resolveStandardEvent({ detail: { didError: false, cart: result.json } });
        document.dispatchEvent(
          new CustomEvent('ff:cart-add-success', {
            bubbles: true,
            detail: {
              productId: self.productId,
              variantId: self.currentVariantId,
              quantity: self.currentQuantity,
              sellingPlanId: self.currentSellingPlanId,
              sourceSectionId: self.root.dataset.sectionId,
              line: result.json,
            },
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function () {
        self.isSubmitting = false;
        self.ctaButton.disabled = false;
        self.ctaButton.textContent = originalLabel;
        if (resolveStandardEvent) resolveStandardEvent({ detail: { didError: true } });
      });
  };

  function initAll() {
    qsa(document, '[data-ff-sticky-purchase-bar]').forEach(function (root) {
      if (root.__ffStickyBar) return;
      root.__ffStickyBar = new FFStickyBar(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    qsa(evt.target, '[data-ff-sticky-purchase-bar]').forEach(function (root) {
      root.__ffStickyBar = null;
      root.__ffStickyBar = new FFStickyBar(root);
    });
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    qsa(evt.target, '[data-ff-sticky-purchase-bar]').forEach(function (root) {
      if (root.__ffStickyBar) {
        root.__ffStickyBar.destroy();
        root.__ffStickyBar = null;
      }
    });
  });
})();
